import { type FC, type FormEvent, useRef } from "react";
import { FaPercent } from "react-icons/fa";
import { HiOutlinePhotograph } from "react-icons/hi";
import { IoIosClose } from "react-icons/io";
import { MoonLoader } from "react-spinners";
import { CampoEntrada } from "../../../pages/HomePage/Components/Modals/CrearClientesModal/CampoEntrada";
import { useCrearClienteExistenteModal } from "./crearClienteExistenteModalHooks";
import type { CrearClienteExistenteModalProps } from "./types";

const MENSAJE_CAMPO_OBLIGATORIO = "Este campo es obligatorio";

const esCampoValidable = (
  element: EventTarget | null,
): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLSelectElement ||
  element instanceof HTMLTextAreaElement;

const mostrarMensajeCampoObligatorio = (event: FormEvent<HTMLFormElement>) => {
  const campo = event.target;

  if (!esCampoValidable(campo)) {
    return;
  }

  campo.setCustomValidity(campo.validity.valueMissing ? MENSAJE_CAMPO_OBLIGATORIO : "");
};

const limpiarMensajeCampoObligatorio = (event: FormEvent<HTMLFormElement>) => {
  const campo = event.target;

  if (!esCampoValidable(campo)) {
    return;
  }

  campo.setCustomValidity("");
};

export const CrearClienteExistenteModal: FC<CrearClienteExistenteModalProps> = (props) => {
  const {
    campos,
    crearCliente,
    datosCliente,
    fechaInicialDisplay,
    fechaInicialState,
    filteredClients,
    handleClose,
    handleCuotasPactadasChange,
    handleFechaInicialChange,
    handleFormaDePagoChange,
    handleImageChange,
    handleRemoveImage,
    handleRemoveImageTienda,
    handleImageTiendaChange,
    handlePorcentajeInteresChange,
    handleSearchTermChange,
    handleSelectedCliente,
    handleValorPrestamoChange,
    imagePreview,
    imageTiendaPreview,
    isSubmiting,
    opcionesFormaPago,
    showInputSearch,
  } = useCrearClienteExistenteModal(props);
  const fechaInicialInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputTiendaRef = useRef<HTMLInputElement | null>(null);
  const imageClienteSrc =
    imagePreview || (typeof datosCliente.image === "string" ? datosCliente.image : null);
  const imageTiendaSrc =
    imageTiendaPreview ||
    (typeof datosCliente.imageTienda === "string" ? datosCliente.imageTienda : null);
  const hasImageCliente = Boolean(imagePreview || datosCliente.image);
  const hasImageTienda = Boolean(imageTiendaPreview || datosCliente.imageTienda);

  const limpiarImagenCliente = () => {
    handleRemoveImage();

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const limpiarImagenTienda = () => {
    handleRemoveImageTienda();

    if (imageInputTiendaRef.current) {
      imageInputTiendaRef.current.value = "";
    }
  };

  return (
    <div className="w-full min-h-screen h-screen z-40 bg-black bg-opacity-50 px-2 md:px-8 flex justify-center items-center fixed ">
      <div className="bg-gray-200 w-[400px] p-4 rounded-md relative max-h-[90%] overflow-y-auto">
        <IoIosClose
          className="absolute right-0 top-0 cursor-pointer"
          size={40}
          onClick={handleClose}
        />
        <h2 className="text-center text-xl font-semibold">Cliente Existente</h2>

        {showInputSearch ? (
          <input
            type="text"
            placeholder="Ingresar nombre de cliente"
            value={props.searchTermExis}
            onChange={handleSearchTermChange}
            className="w-full p-2 mt-2 border rounded-md"
          />
        ) : null}

        {props.searchTermExis &&
          filteredClients.map((cliente) => (
            <div
              key={cliente.uid}
              className="mt-1 p-2 rounded-md cursor-pointer border border-[#8131bd]"
              onClick={() => handleSelectedCliente(cliente)}
            >
              <span className="uppercase font-semibold">{cliente.nombreCliente}</span>
            </div>
          ))}

        {!showInputSearch && (
          <form
            onSubmit={crearCliente}
            onInvalidCapture={mostrarMensajeCampoObligatorio}
            onInputCapture={limpiarMensajeCampoObligatorio}
            onChangeCapture={limpiarMensajeCampoObligatorio}
            className="w-full mt-2"
          >
            <fieldset disabled={isSubmiting} className="w-full flex flex-col gap-2 items-center">
              {imageClienteSrc ? (
                <img
                  src={typeof imageClienteSrc === "string" ? imageClienteSrc : ""}
                  alt="Selected"
                  className="h-[200px] w-auto rounded-r-md object-cover"
                />
              ) : null}

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md relative">
                <label
                  htmlFor="imageInput"
                  className="h-full w-[40px] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400 cursor-pointer"
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    id="imageInput"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <HiOutlinePhotograph size={24} />
                </label>
                <input
                  type="text"
                  className="flex-1 rounded-r-md w-full px-2 focus:border-transparent focus:outline-none"
                  placeholder={hasImageCliente ? "Cambiar Foto" : "Agregar Foto del Cliente"}
                  onClick={() => imageInputRef.current?.click()}
                  readOnly
                />
              </div>

              {hasImageCliente ? (
                <button
                  type="button"
                  className="self-end text-sm text-red-600 underline"
                  onClick={limpiarImagenCliente}
                >
                  Quitar foto del cliente
                </button>
              ) : null}

              {imageTiendaSrc ? (
                <img
                  src={typeof imageTiendaSrc === "string" ? imageTiendaSrc : ""}
                  alt="Selected"
                  className="h-[200px] w-auto rounded-r-md object-cover"
                />
              ) : null}

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md relative">
                <label
                  htmlFor="imageInputTienda"
                  className="h-full w-[40px] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400 cursor-pointer"
                >
                  <input
                    ref={imageInputTiendaRef}
                    type="file"
                    id="imageInputTienda"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageTiendaChange}
                  />
                  <HiOutlinePhotograph size={24} />
                </label>
                <input
                  type="text"
                  className="flex-1 rounded-r-md w-full px-2 focus:border-transparent focus:outline-none"
                  placeholder={hasImageTienda ? "Cambiar Foto" : "Agregar Foto de Tienda"}
                  onClick={() => imageInputTiendaRef.current?.click()}
                  readOnly
                />
              </div>

              {hasImageTienda ? (
                <button
                  type="button"
                  className="self-end text-sm text-red-600 underline"
                  onClick={limpiarImagenTienda}
                >
                  Quitar foto de tienda
                </button>
              ) : null}

              {campos.map(({ Icon, ...campo }) => (
                <CampoEntrada key={campo.placeholder} icono={<Icon size={24} />} {...campo} />
              ))}

              <h2 className="text-center text-xl font-semibold">Datos del Credito</h2>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Fecha Inicial
                </div>
                <input
                  type="text"
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  onClick={() => {
                    fechaInicialInputRef.current?.showPicker?.();
                    fechaInicialInputRef.current?.click();
                  }}
                  value={fechaInicialDisplay}
                  readOnly
                />
                <input
                  ref={fechaInicialInputRef}
                  type="date"
                  className="sr-only"
                  onChange={handleFechaInicialChange}
                  value={fechaInicialState}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>

              <div
                className={`${
                  datosCliente.fechaFinal ? "flex" : "hidden"
                }  w-full h-[40px] border border-gray-400 rounded-md`}
              >
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Fecha Final
                </div>
                <input
                  type="text"
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  value={datosCliente.fechaFinal}
                  readOnly
                />
              </div>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Valor Prestamo
                </div>
                <input
                  type="number"
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  onChange={handleValorPrestamoChange}
                  required
                />
              </div>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Porcentaje de interes
                </div>
                <div className="relative flex-1 w-[50%]">
                  <input
                    type="number"
                    className="w-full h-full rounded-r-md px-2 pr-8 focus:border-transparent focus:outline-none"
                    onChange={handlePorcentajeInteresChange}
                    required
                    step="0.1"
                  />
                  <FaPercent
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    size={14}
                  />
                </div>
              </div>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Cuotas Pactadas
                </div>
                <input
                  type="number"
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  onChange={handleCuotasPactadasChange}
                  required
                />
              </div>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Pago Diario
                </div>
                <input
                  type="number"
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  value={datosCliente.pagoDiario}
                  readOnly
                />
              </div>

              <div className="flex w-full h-[40px] border border-gray-400 rounded-md">
                <div className="h-full w-[50%] bg-gray-200 flex items-center justify-center rounded-l-md border-r border-gray-400">
                  Forma de pago
                </div>
                <select
                  className="flex-1 rounded-r-md w-[50%] px-2 focus:border-transparent focus:outline-none"
                  value={datosCliente.formaDePago}
                  onChange={handleFormaDePagoChange}
                >
                  {opcionesFormaPago.map((opcion) => (
                    <option key={opcion} value={opcion}>
                      {opcion.charAt(0).toUpperCase() + opcion.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="bg-[#8131bd] w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
                disabled={isSubmiting}
              >
                {isSubmiting ? <MoonLoader size={20} color="#ffffff" /> : "Agregar Cliente"}
              </button>
            </fieldset>
          </form>
        )}
      </div>
    </div>
  );
};
